import { Button, HSpacer, InlineMessage, Text, VSpacer } from '@/components/DesignSystem';
import { ApiEnergyProgramDetail } from '@api/interfaces/ApiEnergyProgramDetail';
import CheckCircle from '@mui/icons-material/CheckCircle';
import RadioButtonUnchecked from '@mui/icons-material/RadioButtonUnchecked';
import WarningAmberOutlined from '@mui/icons-material/WarningAmberOutlined';
import { Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';

interface EnergyProgramReadinessProps {
  isSaving: boolean,
  onMarkNotReady: () => void,
  onMarkReady: () => void,
  program: ApiEnergyProgramDetail,
}

export const EnergyProgramReadiness = ({
  isSaving,
  onMarkNotReady,
  onMarkReady,
  program,
}: EnergyProgramReadinessProps) => {
  const { t } = useTranslation();
  const { hasCategoryMapping, hasTiersAndBehaviors } = program.readiness;

  if (program.isReady) {
    return (
      <Stack alignItems="center" direction="row" justifyContent="space-between">
        <InlineMessage
          icon={<CheckCircle color="success" />}
          testID="energy-program-ready"
          title={t(($) => $.energy.readiness.ready)}
        />
        {!program.hasEnergyPoints && (
          <Button
            disabled={isSaving}
            onClick={onMarkNotReady}
            testID="energy-program-mark-not-ready"
            variant="text"
          >
            {t(($) => $.energy.readiness.markNotReady)}
          </Button>
        )}
      </Stack>
    );
  }

  const checklist = [
    {
      isMet: hasCategoryMapping,
      key: 'categoryMapping',
      label: t(($) => $.energy.readiness.categoryMapping),
    },
    {
      isMet: hasTiersAndBehaviors,
      key: 'tiers',
      label: t(($) => $.energy.readiness.tiers),
    },
  ];

  return (
    <Stack data-testid="energy-program-not-ready">
      <InlineMessage
        icon={<WarningAmberOutlined color="warning" />}
        testID="energy-program-not-ready-message"
        title={t(($) => $.energy.readiness.notReady)}
      />
      <VSpacer size="3" />
      {checklist.map(({ isMet, key, label }) => (
        <Stack
          alignItems="center"
          data-testid={`energy-program-readiness-${key}-${isMet ? 'met' : 'unmet'}`}
          direction="row"
          key={key}
          sx={{ pl: '4px', py: '2px' }}
        >
          {isMet
            ? <CheckCircle color="success" fontSize="small" />
            : <RadioButtonUnchecked color="disabled" fontSize="small" />}
          <HSpacer size="3" />
          <Text category="body-medium">{label}</Text>
        </Stack>
      ))}
      <VSpacer size="3" />
      <Stack direction="row">
        <Button
          disabled={isSaving || !hasCategoryMapping || !hasTiersAndBehaviors}
          onClick={onMarkReady}
          testID="energy-program-mark-ready"
        >
          {t(($) => $.energy.readiness.markReady)}
        </Button>
      </Stack>
    </Stack>
  );
};
